/*
Persistent-shell navigation: internal links swap only the content column, so
the topbar and the radio bar — including a playing stream — survive every
page change. Any failure falls back to a normal full load.
*/

const CARD_CSS = 'link[href*="/public/cards.min.css"]';
const CARD_JS = 'script[src*="/public/cards.min.js"]';
const CARD_ASSETS_TIMEOUT_MS = 5000;
const THEME_VERSION_MARKERS = [
    ['script[src*="built/index.js"]', 'src'],
    ['style[data-th-css]', 'data-th-css'],
];

// The document on screen. Back/forward within it (an anchor, the lightbox step) is
// native; any other history entry loads its page. Entries made here carry the id of
// their document (thDoc): two documents can share an address — a link to the page on
// screen loads it again as a new entry. Entries the browser makes itself (anchors)
// carry no id and are told apart by address — path + query.
let loadedPage = pageKey(location.href);
let loadedDoc = null;

export default function pjax() {
    if (!('pushState' in history)) {
        return;
    }
    loadedDoc = stampEntry();

    document.addEventListener('click', (e) => {
        const href = isPlainClick(e) ? swappableHref(e.target.closest('a[href]')) : null;
        if (!href) {
            return;
        }
        e.preventDefault();
        go(href, true);
    });

    // Only the latest navigation may swap the page: an older one still waiting on the
    // network must not overwrite a newer click or a later step through history.
    let latestNav = 0;

    window.addEventListener('popstate', (e) => {
        latestNav++;
        if (isLoadedDocument(e.state)) {
            adoptEntry();
            return;
        }
        go(location.href, false);
    });

    // push — a click: a new history entry. Otherwise Back/forward landed on an entry
    // whose thScrollY is where the reader was on that page when leaving it.
    async function go(href, push) {
        if (!document.querySelector('.th-main')) {
            location.href = href;
            return;
        }
        const nav = ++latestNav;
        try {
            const doc = await fetchDocument(href);
            if (nav !== latestNav) {
                return;
            }
            const next = doc.querySelector('.th-main');
            if (!next) {
                throw new Error('no shell');
            }
            // Theme deployed while this tab was open — its CSS/JS no longer
            // match the fetched markup. Swap would render broken; reload.
            if (themeChanged(doc)) {
                location.href = href;
                return;
            }
            const [, cardScript] = await Promise.all([loadCardStyles(doc), fetchCardScript(doc)]);
            if (nav !== latestNav) {
                return;
            }
            enterHistory(href, push);
            swapIn(doc, next, cardScript);
            scrollAfterSwap(href);
            window.__thPageInit?.();
        } catch (_) {
            if (nav === latestNav) {
                location.href = href;
            }
        }
    }

    // Puts the fetched page on screen: content, title, body classes, the shell parts
    // that change between pages, and the page's own scripts.
    function swapIn(doc, next, cardScript) {
        document.querySelector('.th-main').replaceWith(next);
        document.title = doc.title;
        document.body.className = doc.body.className;
        syncTicker(doc);
        syncHead(doc);
        syncTabs(doc);
        runScripts(next);
        if (cardScript) {
            runCardScript(cardScript);
        }
    }

    // To the anchor in the address if the page has it, otherwise where the reader was
    // on this page before (Back) or to the top. Read after enterHistory, which may
    // have dropped a position that means nothing on this page.
    function scrollAfterSwap(href) {
        const anchor = new URL(href).hash.slice(1);
        const target = anchor && document.getElementById(decodeURIComponentSafe(anchor));
        if (target) {
            target.scrollIntoView();
            return;
        }
        const restoreY = history.state?.thScrollY;
        window.scrollTo(0, Number.isFinite(restoreY) ? restoreY : 0);
    }

    function decodeURIComponentSafe(value) {
        try {
            return decodeURIComponent(value);
        } catch (_) {
            return value;
        }
    }

    // The ticker lives outside .th-main, below the category tabs, and only exists on
    // the home page.
    function syncTicker(doc) {
        const cur = document.querySelector('.th-ticker');
        const next = doc.querySelector('.th-ticker');
        if (cur && !next) {
            cur.remove();
        } else if (!cur && next) {
            document.querySelector('.th-sections')?.after(next);
        }
    }

    function syncHead(doc) {
        // The head names the page on screen: links and structured data come from the new
        // page's head (the post template's NewsArticle is built by its own script on arrival,
        // it is not in the fetched head, so nothing doubles).
        ['link[rel="next"]', 'link[rel="prev"]', 'link[rel="canonical"]'].forEach((sel) => {
            document.head.querySelector(sel)?.remove();
            const el = doc.head.querySelector(sel);
            if (el) {
                document.head.appendChild(el.cloneNode());
            }
        });
        const jsonLd = 'script[type="application/ld+json"]';
        document.head.querySelectorAll(jsonLd).forEach((s) => s.remove());
        doc.head.querySelectorAll(jsonLd).forEach((s) => {
            const copy = document.createElement('script');
            copy.type = 'application/ld+json';
            copy.textContent = s.textContent;
            document.head.appendChild(copy);
        });
    }

    // The theme JS and the inline CSS each carry their version in an {{asset}}
    // URL (default.hbs); either one changing means a redeploy.
    function themeChanged(doc) {
        return THEME_VERSION_MARKERS.some(([selector, attr]) => {
            const fresh = doc.querySelector(selector)?.getAttribute(attr);
            const current = document.querySelector(selector)?.getAttribute(attr);
            return fresh && current && fresh !== current;
        });
    }

    // Ghost's card assets (default.hbs puts them on post/page templates only)
    // are fully in hand BEFORE the swap: the stylesheet loaded, the script
    // fetched as text. Any failure or timeout rejects and go() falls back to a
    // full load, so nothing can break after the new content is on screen.
    // Parallel navigations share the requests; a failed one is forgotten, so
    // the next click retries.
    let cardStyles = null;
    const cardScripts = new Map();

    function loadCardStyles(doc) {
        const link = doc.head.querySelector(CARD_CSS);
        if (!link) {
            return null;
        }
        if (!cardStyles) {
            cardStyles = hasLoadedCardStyles() ? Promise.resolve() : appendCardStyles(link);
        }
        return cardStyles;
    }

    // A post opened directly already has the stylesheet from ghost_head; it
    // counts only if it actually loaded (a failed request leaves `sheet` null),
    // otherwise it is replaced by a fresh request.
    function hasLoadedCardStyles() {
        const existing = document.head.querySelector(CARD_CSS);
        if (existing && !existing.sheet) {
            existing.remove();
        }
        return Boolean(existing?.sheet);
    }

    function appendCardStyles(link) {
        const added = link.cloneNode();
        const loaded = new Promise((resolve, reject) => {
            added.onload = resolve;
            added.onerror = () => reject(new Error('card styles failed'));
        });
        document.head.appendChild(added);
        return withTimeout(loaded).catch((err) => {
            added.remove();
            cardStyles = null;
            throw err;
        });
    }

    function fetchCardScript(doc) {
        const src = doc.head.querySelector(CARD_JS)?.getAttribute('src');
        if (!src) {
            return null;
        }
        if (!cardScripts.has(src)) {
            const text = withTimeout(fetch(src).then((res) => {
                if (!res.ok) {
                    throw new Error('card script http ' + res.status);
                }
                return res.text();
            })).catch((err) => {
                cardScripts.delete(src);
                throw err;
            });
            cardScripts.set(src, text);
        }
        return cardScripts.get(src);
    }

    // Ghost's card scripts (video player, gallery, audio, toggle) scan the
    // document once when they run. An inline script executes synchronously on
    // insertion, so they see exactly the content just swapped in — no second,
    // late run over some newer page.
    function runCardScript(text) {
        const s = document.createElement('script');
        s.text = text;
        document.body.appendChild(s);
        s.remove();
    }

    function withTimeout(promise) {
        const timeout = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('card assets timeout')), CARD_ASSETS_TIMEOUT_MS);
        });
        return Promise.race([promise, timeout]);
    }

    // The tabs row persists, so the active chip must be re-pointed.
    function syncTabs(doc) {
        const current = '.th-tab';
        const fresh = new Map();
        doc.querySelectorAll(current).forEach(t => fresh.set(t.getAttribute('href'), t.getAttribute('aria-current')));
        document.querySelectorAll(current).forEach(t => {
            const v = fresh.get(t.getAttribute('href'));
            if (v) {
                t.setAttribute('aria-current', v);
            } else {
                t.removeAttribute('aria-current');
            }
        });
    }

    // Scripts inside parsed HTML are inert — recreate them so post-page
    // extras (NewsArticle JSON-LD, embed loaders) actually run. X posts are not
    // rendered here: they open on a click only (tweetEmbeds.js).
    function runScripts(scope) {
        scope.querySelectorAll('script').forEach((old) => {
            const s = document.createElement('script');
            for (const { name, value } of old.attributes) {
                s.setAttribute(name, value);
            }
            s.text = old.text;
            old.replaceWith(s);
        });
    }
}

// The page at `href`, parsed; throws on a network error or a non-2xx answer.
async function fetchDocument(href) {
    const res = await fetch(href);
    if (!res.ok) {
        throw new Error('http ' + res.status);
    }
    return new DOMParser().parseFromString(await res.text(), 'text/html');
}

// A left click with no modifier keys: the others open a new tab or window.
function isPlainClick(e) {
    const modified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    return !e.defaultPrevented && e.button === 0 && !modified;
}

// The address to load in place for a click on `link`, or null where the browser must
// handle it: another window, a download, another site, an in-page anchor or #/search,
// Ghost's own routes.
function swappableHref(link) {
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) {
        return null;
    }
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.hash) {
        return null;
    }
    return /^\/(ghost|members|api|content|rss)\//.test(url.pathname) ? null : url.href;
}

// Whether the history entry Back/forward landed on shows the document on screen.
function isLoadedDocument(entry) {
    if (entry?.thDoc) {
        return entry.thDoc === loadedDoc;
    }
    return pageKey(location.href) === loadedPage;
}

// An entry the browser made within the document on screen (an anchor) gets its id,
// so coming back to it from another page later does not tell it apart from the rest.
function adoptEntry() {
    if (!history.state?.thDoc) {
        history.replaceState({ ...history.state, thDoc: loadedDoc }, '');
    }
}

// Records a swap in history: a click saves where the reader was and adds an entry;
// Back/forward keeps the entry it landed on.
function enterHistory(href, push) {
    if (push) {
        history.replaceState({ ...history.state, thScrollY: window.scrollY }, '');
        history.pushState({}, '', href);
    }
    loadedDoc = stampEntry();
    loadedPage = pageKey(href);
}

// Id of the current history entry's document; an entry without one gets a new id.
function stampEntry() {
    const state = history.state ?? {};

    if (state.thFeedMoved) {
        return restampMovedEntry(state);
    }
    if (state.thDoc) {
        return state.thDoc;
    }
    const id = newDocId();

    history.replaceState({ ...state, thDoc: id }, '');
    return id;
}

// An entry the theme before 19.10.2026 wrote when "Show more" moved the address to a
// later page, still in tabs opened before then: a document apart from the feed it was
// moved in, and the position it kept in that long feed means nothing on the page loaded
// now. Kept while such tabs can exist — the browser holds a tab's history for its life.
function restampMovedEntry(state) {
    const id = newDocId();
    const fresh = { ...state, thDoc: id };

    delete fresh.thFeedMoved;
    delete fresh.thScrollY;
    history.replaceState(fresh, '');
    return id;
}

// Unique enough to tell apart the entries of one tab; crypto.randomUUID is missing
// in older browsers, and this runs before every other module.
function newDocId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function pageKey(href) {
    const url = new URL(href, location.href);
    return url.pathname + url.search;
}
